import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import HeroVideoBackdrop from '../components/HeroVideoBackdrop';
import FAQ from '../components/FAQ';
import CountUp from '../components/CountUp';
import PartnersSection from '../components/PartnersSection';
import { CEREMONY_LINKS } from '../data/event';
import { useWinnersStatus } from '../hooks/useWinnersStatus';
import { KEY_DATES, WHY_ENTER_DETAILED, KEY_HIGHLIGHTS, ENTRANTS_DESCRIBED } from '../data/content';
import { Handshake, Crown } from 'lucide-react';
import { GoldDust, KineticMarquee, GoldSweepDivider, LightLeak, KineticHeadline } from '../components/Creative';
import { useContentSettings } from '../hooks/useContentSettings';
import { Seo, eventSchema, webPageSchema, faqSchema } from '../components/Seo';

const HIGHLIGHT_ICONS = { Handshake, Crown };
const MARQUEE_WORDS = ['Creativity', 'Strategy', 'Impact', 'Innovation', 'Storytelling', 'Influence', 'Brilliance', 'Resonance', 'Distinction', 'Excellence'];

// Static fallback timeline shown ONLY when admin toggles reveal_full_timeline
// on. Kept in sync with `KEY_DATES` from `data/content.js` (source of truth).
const FULL_TIMELINE = [
  { label: 'Nominations Close', date: '6 September 2026',   state: 'open' },
  { label: 'Jury Evaluation',   date: '15–30 September 2026', state: 'upcoming' },
  { label: 'Awards Night',      date: '8 October 2026', venue: 'Aerocity, New Delhi', state: 'final' },
];

const Home = () => {
  const cms = useContentSettings();
  const winnersAnnounced = useWinnersStatus();
  const tagline = cms.hero_tagline || 'Celebrating the power of brand impact.';
  const dates = cms.reveal_full_timeline ? FULL_TIMELINE : KEY_DATES;
  return (
    <div data-testid="page-home">
      <Seo
        title="AdAura Awards | India's Biggest Marketing, Advertising & Brand Awards 2026"
        description="AdAura Awards 2026 — India's most prestigious marketing awards recognising excellence across advertising, digital, social media, influencer, PR, brand, agency, startup and creator work. Nominations open. Presented by Marketing Mind."
        path="/"
        imageAlt="AdAura Awards 2026 · Presented by Marketing Mind"
        jsonLd={[
          webPageSchema({ path: '/', name: 'AdAura Awards 2026', description: "India's premier marketing awards celebrating brand impact." }),
          eventSchema({
            startDate: '2026-10-08T19:00:00+05:30', endDate: '2026-10-08T23:00:00+05:30',
            url: 'https://adauraawards.com/',
            description: 'The AdAura Awards 2026 — India\'s premier marketing awards across 50+ categories in advertising, digital, brand, social, influencer, PR, agency and startup excellence. Awards Night: 8 October 2026 · Aerocity, New Delhi.',
            venueName: 'Aerocity, New Delhi',
            venueLocality: 'New Delhi',
          }),
          faqSchema([
            { q: 'What are the AdAura Awards?', a: 'The AdAura Awards 2026 are India\'s premier marketing awards, presented by Marketing Mind. They recognise the country\'s finest work in advertising, digital, social media, influencer, PR, brand, agency, startup and creator categories.' },
            { q: 'Who can enter the AdAura Awards?', a: 'Brands, agencies, startups, creators, PR firms, media houses and marketing teams working on campaigns that reached Indian audiences during the eligibility window can enter across 50+ categories.' },
            { q: 'When do nominations close?', a: 'Nominations for the 2026 edition close on 6 September 2026. Register on adauraawards.com/enter to submit your work.' },
            { q: 'How is the AdAura jury selected?', a: 'The AdAura jury comprises senior CMOs, agency leaders, brand strategists and marketing veterans across India. Every entry is judged blind on measurable business impact.' },
            { q: 'What is the entry fee for the AdAura Awards?', a: 'The AdAura Awards entry fee is INR 7,500 (excluding 18% GST) per nomination. Payment is accepted online via Razorpay or offline via bank/UPI transfer.' },
          ]),
        ]}
      />
      {/* HERO - centred, video-backed */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        <HeroVideoBackdrop />
        <GoldDust density={10} />

        {/* Decorative gold frame */}
        <div aria-hidden className="absolute inset-x-6 top-24 bottom-12 md:inset-x-12 md:top-28 md:bottom-16 border border-[#C9A56C]/20 pointer-events-none z-[2]" />

        <div className="content-container w-full relative z-[3] pt-24 pb-16 md:pt-28 md:pb-20">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            <Reveal delay={120}>
              <div className="logo-stage logo-stage-sm">
                <span aria-hidden className="logo-ring-outer" />
                <span aria-hidden className="logo-ring" />
                <div className="logo-circle">
                  <img
                    src="/adaura-logo.png"
                    alt="AdAura Awards 2026 · India's Premier Marketing Awards presented by Marketing Mind"
                    data-testid="hero-logo"
                    width="480"
                    height="480"
                    fetchPriority="high"
                    decoding="async"
                    className="logo-image"
                  />
                  <span aria-hidden className="logo-shimmer" />
                </div>
              </div>
            </Reveal>

            <Reveal delay={260}>
              <div className="flex items-center justify-center gap-4 mt-8 md:mt-10">
                <span className="h-px w-8 bg-[#B8884A]" />
                <span className="font-sans text-[11px] tracking-[0.35em] uppercase text-[#3A2418] font-semibold">
                  Marketing Mind Presents
                </span>
                <span className="h-px w-8 bg-[#B8884A]" />
              </div>
            </Reveal>

            <Reveal delay={360}>
              <h1 className="display text-[#2D1810] mt-8 text-[2.4rem] sm:text-[3.2rem] md:text-[4rem] lg:text-[5rem] leading-[1] tracking-[-0.025em] font-semibold" data-testid="hero-title">
                The{' '}
                <span
                  className="brand-mark"
                  style={{
                    /* Deep warm champagne — sampled from the AdAura logo's
                       metallic tone. Definitively warm (no blue cast) and
                       ~9:1 contrast on the cream hero background, so it
                       reads as premium metallic and stays clearly legible.
                       Previous #3E3F42 rendered blue-ish on some monitors. */
                    color: '#5C4218',
                    fontWeight: 700,
                  }}
                >
                  AdAura
                </span>{' '}
                Awards 2026
              </h1>
            </Reveal>

            <Reveal delay={460}>
              <p className="mt-7 italic text-[#3A2418] text-base md:text-lg lg:text-xl tracking-[-0.005em] leading-[1.4]" data-testid="hero-tagline">
                {tagline}
              </p>
            </Reveal>

            <Reveal delay={580}>
              <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-4">
                {CEREMONY_LINKS.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    data-testid={`hero-cta-${c.to.replace('/', '')}`}
                    className="inline-flex flex-col items-center justify-center border border-[var(--walnut)] text-[var(--walnut)] hover:bg-[var(--walnut)] hover:text-white px-7 py-2.5 leading-none transition-colors group"
                  >
                    <span className="uppercase tracking-[0.22em] text-[11px] font-medium">{c.label}</span>
                    <span className="mt-1.5 text-[9px] tracking-[0.12em] uppercase opacity-70 group-hover:opacity-90">{winnersAnnounced && c.liveNote ? 'Winners announced · View now' : c.note}</span>
                  </Link>
                ))}
              </div>
            </Reveal>

            <div className="mt-9 md:mt-10 max-w-xl mx-auto text-center fade-up visible" data-testid="hero-event-details">
              <div className="flex items-center justify-center gap-4 mb-3">
                <span aria-hidden className="h-px w-16 bg-[#B8884A]" />
              </div>
              <div className="font-serif text-[#2D1810] text-2xl md:text-3xl leading-tight tracking-tight" data-testid="hero-event-date">
                8 October 2026
              </div>
              <div className="mt-2 text-[12px] md:text-[13px] tracking-[0.25em] uppercase text-[#634F3A]" data-testid="hero-event-venue">
                Aerocity · New Delhi
              </div>
            </div>
          </div>
        </div>

        {/* Corner ticks */}
        <span aria-hidden className="absolute left-6 md:left-12 top-24 md:top-28 w-3 h-3 border-t border-l border-[#C9A56C] z-[2]" />
        <span aria-hidden className="absolute right-6 md:right-12 top-24 md:top-28 w-3 h-3 border-t border-r border-[#C9A56C] z-[2]" />
        <span aria-hidden className="absolute left-6 md:left-12 bottom-12 md:bottom-16 w-3 h-3 border-b border-l border-[#C9A56C] z-[2]" />
        <span aria-hidden className="absolute right-6 md:right-12 bottom-12 md:bottom-16 w-3 h-3 border-b border-r border-[#C9A56C] z-[2]" />
      </section>

      {/* Partners ribbon — subtle acknowledgement, no dedicated section */}
      <PartnersSection variant="inline" />


      {/* ABOUT - direct copy, no header */}
      <section className="section-pad bg-[#634F3A] relative overflow-hidden" data-testid="home-about-adaura">
        <LightLeak position="top-left" />
        <div className="content-container relative z-10">
          <div className="grid grid-cols-12 gap-y-10 md:gap-10 lg:gap-16">
            <div className="col-span-12 lg:col-span-10 lg:col-start-2">
              <Reveal>
                <p className="font-serif text-white/90 text-xl md:text-2xl lg:text-[1.75rem] leading-[1.5] tracking-[-0.005em]">
                  <span className="text-[#C9A56C]">AdAura Awards</span> celebrates the campaigns, brands, agencies and marketers whose
                  work created an <span className="italic text-[#E8C77F]">unmistakable aura</span> in the market - shaping conversations,
                  influencing culture and driving measurable brand impact.
                </p>
              </Reveal>
              <Reveal delay={180}>
                <p className="font-sans text-white/65 text-[15px] md:text-[16px] leading-[1.9] mt-8 max-w-4xl">
                  In a world overflowing with content and communication, only a few ideas truly
                  leave a mark. AdAura recognises the work that went beyond visibility to create
                  relevance, influence consumer behaviour and deliver meaningful business
                  outcomes. From breakthrough creativity and social-first storytelling to
                  AI-powered innovation and integrated marketing excellence, AdAura honours the
                  ideas that didn&apos;t just get noticed - they made an impact.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* KEY HIGHLIGHTS */}
      <section className="section-pad relative overflow-hidden" data-testid="home-key-highlights">
        <LightLeak position="top-right" />
        <div className="content-container relative z-10">
          <Reveal><div className="eyebrow mb-5">Key Highlights</div></Reveal>
          <Reveal delay={120}>
            <KineticHeadline
              text="An evening built at the intersection of scale, craft and consequence."
              as="h2"
              className="display text-white/95 text-2xl md:text-3xl lg:text-4xl tracking-[-0.015em] leading-[1.12] max-w-3xl"
            />
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#C9A56C]/15">
            {KEY_HIGHLIGHTS.map((k, i) => {
              const Icon = k.icon ? HIGHLIGHT_ICONS[k.icon] : null;
              return (
                <Reveal key={k.t} delay={i * 90} className="bg-[#5A4733] p-7 md:p-8 min-h-[200px] flex flex-col justify-between lift-on-hover">
                  {k.n ? (
                    <div className="numeral text-4xl md:text-5xl"><CountUp value={k.n} /></div>
                  ) : Icon ? (
                    <span
                      aria-hidden
                      className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#C9A56C]/40"
                      style={{ boxShadow: 'inset 0 0 0 1px rgba(201,165,108,0.08)' }}
                    >
                      <Icon className="w-5 h-5 text-[#C9A56C]" strokeWidth={1.4} />
                    </span>
                  ) : (
                    <span aria-hidden className="block w-8 h-px bg-[#C9A56C]/50" />
                  )}
                  <p className="font-serif text-white text-base md:text-lg leading-snug">{k.t}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GOLD SWEEP DIVIDER */}
      <GoldSweepDivider label="The Road · The Night · The Aura" />

      {/* KEY DATES */}
      <section className="section-pad bg-[#634F3A] relative overflow-hidden" data-testid="home-key-dates">
        <LightLeak position="bottom-right" />
        <div className="content-container relative z-10">
          <Reveal><div className="eyebrow mb-5">Key Dates · Edition 2026</div></Reveal>
          <Reveal delay={120}>
            <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-4xl tracking-[-0.015em] leading-[1.12] max-w-2xl">
              The road to <span className="text-[#C9A56C]">the night.</span>
            </h2>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-12 relative">
              {/* When we later have multiple key dates, this grid will fan
                  out across 5 columns again. For now (single deadline),
                  we center the one card at ~half width. */}
              <div className={`grid gap-x-6 gap-y-8 relative ${dates.length > 1 ? 'grid-cols-1 md:grid-cols-5' : 'grid-cols-1 md:grid-cols-2 md:max-w-2xl'}`}>
                {dates.length > 1 && (
                  <span aria-hidden className="absolute left-0 right-0 top-[18px] h-px bg-[#C9A56C]/25 hidden md:block" />
                )}
                {dates.map((k, i) => (
                  <div key={k.label} className="relative">
                    <span
                      aria-hidden
                      className={`block w-[10px] h-[10px] rounded-full mb-5 ml-0 ${
                        k.state === 'open' ? 'bg-[#C9A56C]' : k.state === 'final' ? 'bg-[#C9A56C]' : 'bg-[#C9A56C]/40'
                      }`}
                      style={k.state === 'open' ? { boxShadow: '0 0 0 6px rgba(201, 165, 108,0.18)' } : {}}
                    />
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A56C] mb-3">{k.label}</div>
                    <div className="font-serif text-white text-2xl md:text-3xl tracking-tight leading-tight">{k.date}</div>
                    {k.venue && (
                      <div className="text-[12.5px] text-white/70 mt-2 leading-snug italic font-serif" data-testid={`key-date-venue-${i}`}>
                        {k.venue}
                      </div>
                    )}
                    {k.state === 'open' && (
                      <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C9A56C]/85">
                        <span className="w-1.5 h-1.5 bg-[#C9A56C] inline-block animate-pulse" /> Now Open
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {dates.length === 1 && (
                <p className="text-[12px] text-white/55 mt-8 italic max-w-lg font-serif" data-testid="key-dates-note">
                  Additional milestones — jury week and the AdAura Awards night — will be announced in due course.
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY ENTER - 5 reasons */}
      <section className="section-pad relative overflow-hidden" data-testid="home-why-enter">
        <LightLeak position="top-left" />
        <div className="content-container relative z-10">
          <div className="grid grid-cols-12 gap-6 mb-12 md:mb-14">
            <div className="col-span-12 md:col-span-7">
              <Reveal><div className="eyebrow mb-5">Why Enter</div></Reveal>
              <Reveal delay={120}>
                <h2 className="display text-white/95 text-2xl md:text-3xl lg:text-4xl tracking-[-0.015em] leading-[1.12]">
                  Why Enter The <span className="text-[#C9A56C]">AdAura Awards</span>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#C9A56C]/15">
            {WHY_ENTER_DETAILED.map((w, i) => (
              <Reveal key={w.n} delay={i * 80} className="bg-[#5A4733] p-7 md:p-8 min-h-[280px] flex flex-col lift-on-hover">
                <div className="flex items-start justify-between mb-7">
                  <span className="numeral text-3xl md:text-4xl">{w.n}</span>
                  <span className="text-[9px] tracking-[0.3em] uppercase text-white/30">{w.n} / 05</span>
                </div>
                <h3 className="font-serif text-white text-lg md:text-xl leading-tight">{w.t}</h3>
                <p className="text-white/55 text-[13px] md:text-[14px] mt-4 leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO SHOULD ENTER */}
      <section className="section-pad bg-[#634F3A]" data-testid="home-who-should-enter">
        <div className="content-container">
          <div className="grid grid-cols-12 gap-y-10 md:gap-10 lg:gap-16 mb-12 md:mb-16">
            <div className="col-span-12 lg:col-span-5">
              <Reveal><div className="eyebrow mb-5">Who Should Enter</div></Reveal>
              <Reveal delay={120}>
                <p className="font-serif text-white/85 text-lg md:text-xl lg:text-2xl leading-[1.5] tracking-[-0.005em] max-w-md">
                  AdAura welcomes entries from organisations and professionals redefining
                  modern marketing and creating <span className="text-[#C9A56C]">meaningful brand impact.</span>
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-6 flex md:items-end">
              <Reveal delay={200}>
                <p className="text-white/60 text-[14px] md:text-[15px] leading-[1.9] max-w-xl">
                  Whether you&apos;re a global brand, a fast-growing startup, an agency or an
                  in-house team - if your work has shaped conversations, influenced audiences
                  and delivered measurable results, AdAura is for you.
                </p>
              </Reveal>
            </div>
          </div>

          <Reveal delay={220}>
            <div className="flex items-center gap-5 mb-7">
              <div className="eyebrow">Eligible Entrants</div>
              <span className="flex-1 h-px bg-[#C9A56C]/25" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/30">14 Categories</span>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-px gap-y-px bg-[#B8884A]/35 border border-[#B8884A]/40" data-testid="home-entrants-grid">
              {ENTRANTS_DESCRIBED.map((e, i) => (
                <div
                  key={e.t}
                  className="px-6 md:px-8 py-6 md:py-7 group transition-colors"
                  style={{ background: '#F5EDE0' }}
                  onMouseEnter={(ev) => (ev.currentTarget.style.background = '#EBE0CE')}
                  onMouseLeave={(ev) => (ev.currentTarget.style.background = '#F5EDE0')}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="numeral text-base md:text-lg flex-shrink-0" style={{ color: '#9F6A2C' }}>{String(i + 1).padStart(2, '0')}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-[15.5px] md:text-base leading-tight mb-2" style={{ color: '#2D1810' }}>{e.t}</h4>
                      <p className="text-[12.5px] md:text-[13px] leading-[1.65]" style={{ color: 'rgba(45,24,16,0.65)' }}>{e.d}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* FINAL CTA */}
      <section className="section-pad bg-[#634F3A]">
        <div className="content-container">
          <div className="border border-[#C9A56C]/30 p-10 md:p-16 lg:p-20 relative overflow-hidden">
            <span aria-hidden className="absolute -top-px left-1/2 -translate-x-1/2 h-3 w-[60%] border-t border-[#C9A56C]/30" />
            <div className="text-center max-w-3xl mx-auto">
              <div className="eyebrow mb-6">The Last Word</div>
              <h2 className="display text-white text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
                The room is being curated.
                <br />
                <span className="text-[#C9A56C]">Relive the night.</span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to="/winners" className="btn-solid" data-testid="home-final-cta-winners">
                  View Winners
                </Link>
                <Link to="/gallery" className="btn-ghost" data-testid="home-final-cta-gallery">
                  Photo Gallery
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
